import type { Id } from "@convex/_generated/dataModel";
/* vortex-allow-color-file: thermal receipt/ticket print output — rendered on physical paper, literal ink colors required. */
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useDebouncedCallback } from "use-debounce";

import { locationReceiptSettingsUpdateKitchenTicketSettings } from "~/api-refs";
import { convexClient } from "~/integrations/convex/provider";
import { toConvexTableId } from "~/lib/convex-table-id";
import { ProductEvents } from "~/lib/posthog";

import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { Switch } from "../ui/switch";

export interface KitchenTicketReceiptSettings {
  showOrderName: boolean;
  showServerName: boolean;
  showOrderNotes: boolean;
  showSeatNumbers: boolean;
  showVoidItems: boolean;
  reprintAfterAddingItems: boolean;
  reprintAfterVoidingItems: boolean;
}

export function KitchenTicketReceiptSettingsForm({
  settings,
  locationId,
}: {
  settings: KitchenTicketReceiptSettings | null;
  locationId: string;
}) {
  const updateSettingsMutation = useMutation({
    mutationFn: (data: {
      locationId: Id<"locations">;
      value: KitchenTicketReceiptSettings;
    }) =>
      convexClient.mutation(
        locationReceiptSettingsUpdateKitchenTicketSettings,
        data
      ),
    onSuccess: () => {
      ProductEvents.settingsUpdated("kitchen-ticket");
    },
  });

  const form = useForm<KitchenTicketReceiptSettings>({
    defaultValues: settings ?? {
      showOrderName: true,
      showServerName: true,
      showOrderNotes: true,
      showSeatNumbers: true,
      showVoidItems: true,
      reprintAfterAddingItems: true,
      reprintAfterVoidingItems: true,
    },
  });

  const [
    showServerName,
    showOrderName,
    showOrderNotes,
    showSeatNumbers,
    showVoidItems,
    reprintAfterAddingItems,
  ] = form.watch([
    "showServerName",
    "showOrderName",
    "showOrderNotes",
    "showSeatNumbers",
    "showVoidItems",
    "reprintAfterAddingItems",
  ]);

  const update = useDebouncedCallback(() => {
    updateSettingsMutation.mutate({
      locationId: toConvexTableId("locations", locationId),
      value: form.getValues(),
    });
  }, 1000);

  return (
    <div className="flex gap-6" id="kitchen-receipt-info">
      <div className="bg-muted rounded-lg p-6" id="kitchen-receipt">
        <div className="caption bg-background text-foreground shadow-ambient w-56 rounded-md px-3.5 py-7 font-mono">
          <div className="text-center">
            Order: B1
            <br />
            Dine In
            {showServerName && (
              <>
                <br />
                Server: John
              </>
            )}
            <br />
            05/01/2024 4:20 PM
            {showOrderName && (
              <>
                <br />
                Table 3
              </>
            )}
            {showOrderNotes && (
              <>
                <br />
                Peanut alergy
              </>
            )}
          </div>
          <hr className="my-3.5 h-px border-none bg-black" />
          <div className="whitespace-pre">{`${showSeatNumbers ? "Seat 1\n" : ""}2 Cheeseburger
            1 Chicken Burger
              2 Bacon
              1 Tomato
            1 Houseburger
              - Double Patty
              - Ketchup
                1 Regular
                1 Spicy
              - Fries
                - Cajun Spice${showSeatNumbers ? "\nSeat 2" : ""}
            1 Caesar Salad
            1 Latte
              - Regular
              1 Vanilla${
                showVoidItems
                  ? `
            1 BBQ Wings                 VOID
              - Extra Sauce             VOID`
                  : ""
              }`}</div>
          {reprintAfterAddingItems && (
            <div className="font-bold whitespace-pre">{`1 Flaming Wings
              - Extra Sauce`}</div>
          )}
        </div>
      </div>
      <div className="w-full flex-1" id="receipt-info">
        <Form {...form}>
          <form className="space-y-4">
            <div>
              <h3 className="subtitle-sm text-foreground py-5 text-balance">
                Receipt Information
              </h3>
              <hr className="bg-muted mb-4 h-px border-none" />
            </div>
            <FormField
              control={form.control}
              name="showServerName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="label text-foreground flex flex-row items-center gap-2">
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={(v) => {
                          field.onChange(v);
                          update();
                        }}
                      />
                    </FormControl>
                    Show server name
                  </FormLabel>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="showOrderName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="label text-foreground flex flex-row items-center gap-2">
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={(v) => {
                          field.onChange(v);
                          update();
                        }}
                      />
                    </FormControl>
                    Show order name
                  </FormLabel>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="showOrderNotes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="label text-foreground flex flex-row items-center gap-2">
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={(v) => {
                          field.onChange(v);
                          update();
                        }}
                      />
                    </FormControl>
                    Show order notes
                  </FormLabel>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="showSeatNumbers"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="label text-foreground flex flex-row items-center gap-2">
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={(v) => {
                          field.onChange(v);
                          update();
                        }}
                      />
                    </FormControl>
                    Show seat numbers
                  </FormLabel>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="showVoidItems"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="label text-foreground flex flex-row items-center gap-2">
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={(v) => {
                          field.onChange(v);
                          update();
                        }}
                      />
                    </FormControl>
                    Show void items
                  </FormLabel>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div>
              <h3 className="subtitle-sm text-foreground py-5 text-balance">
                Print Settings
              </h3>
              <hr className="bg-muted mb-4 h-px border-none" />
            </div>
            <FormField
              control={form.control}
              name="reprintAfterVoidingItems"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="label text-foreground flex flex-row items-center gap-2">
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={(v) => {
                          field.onChange(v);
                          update();
                        }}
                      />
                    </FormControl>
                    <div className="font-medium">
                      Reprint order after voiding items
                      <FormDescription className="body-sm text-foreground">
                        Kitchen receipts will be reprinted with void items
                        shown.
                      </FormDescription>
                    </div>
                  </FormLabel>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="reprintAfterAddingItems"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="label text-foreground flex flex-row items-center gap-2">
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={(v) => {
                          field.onChange(v);
                          update();
                        }}
                      />
                    </FormControl>
                    <div className="font-medium">
                      Reprint order after adding items
                      <FormDescription className="body-sm text-foreground">
                        Kitchen receipts will be reprinted when new items are
                        added and sent to kitchen.
                      </FormDescription>
                    </div>
                  </FormLabel>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>
      </div>
    </div>
  );
}
